<template>
  <div id="AudioRoom">
    <h3 v-if="audioRoomTitle" class="audio-room-title text-center pb-3 px-3 brand-secondary">
      {{ audioRoomTitle }}
    </h3>
    <div class="col-md-12 no-gutters px-0 relative" id="remote_media_box">
      <transition name="fade">
        <div v-if="general_status === 'connected'" class="animate__animated animate__fadeIn">
          <div class="d-flex flex-column justify-content-center align-items-center py-5">
            <i class="fa-solid fa-3x fa-microphone brand-text pb-3"></i>
            <span>In call</span>
          </div>
        </div>
        <div v-else id="remote_media_placeholder"
          class="animate__animated animate__fadeIn d-flex justify-content-center align-items-center" :class="{
            'test-local-video-placeholder': meetingStatus === 'testRoom',
          }">
          <p class="status-message" v-html="status_message"></p>
        </div>
      </transition>

      <transition name="fade">
        <div v-if="general_status === 'connected'" id="remote_media_status_container"
          class="animate__animated animate__fadeIn">
          <div key="a1" v-if="remote_audio_enabled" class="remote_vid_button mic  animate__animated animate__fadeIn">
            <div v-b-tooltip.hover title="Remote Audio is ON" class="bt_inner">
              <i class="fa fa-microphone" aria-hidden="true"></i>
            </div>
          </div>
          <div key="a2" v-else class="remote_vid_button mic off  animate__animated animate__fadeIn">
            <div v-b-tooltip.hover title="Remote Audio is OFF" class="bt_inner off">
              <i class="fa fa-microphone-slash" aria-hidden="true"></i>
            </div>
          </div>
        </div>
        <div v-else></div>
      </transition>
      <p class="text-contact">
        If you are experiencing any issues with your appointment, please contact
        {{ supportEmail }}
      </p>
    </div>

    <div class="utilities_container">
      <div id="video_buttons">
        <div id="remote_audio"></div>
        <div class="vid_button mic animate__animated animate__fadeIn">
          <button class="bt_inner" :class="{ off: !audio_enabled }" style="margin: auto" @click="toggleAudio">
            <i class="fas" :class="audio_enabled ? 'fa-microphone' : 'fa-microphone-slash'"></i>
          </button>
          <span v-if="!video_processors_supported">Your microphone is <b>{{ audio_enabled ? "ON" : "OFF" }}</b></span>
        </div>
      </div>
      <button v-if="buttonText" class="audio-room-button mt-5" :disabled="permissionsBlocked"
        @click="handleButtonClick">
        {{ buttonText }}
      </button>
    </div>
  </div>
</template>

<script>
const Video = require("twilio-video");
const { connect, LocalAudioTrack, RemoteAudioTrack } = Video;

import * as VideoProcessors from "@twilio/video-processors";
import moment from "moment";
import { formatAppointmentTimeForApi } from "@/utils/appointmentTimezone/appointmentTimezone";
import screenWakeLockMixin from "@/mixins/screenWakeLockMixin";
import { devLog } from "@/utils/devLog/devLog";
import { getSupportEmail } from "@/utils/supportEmail/supportEmail";
import { isEmbedded } from "@/utils/embed/embed";
import { notifyPartner } from "@/utils/embed/partnerEmbed";

import {
  initialiseTwilioLogger,
  startTwilioSession,
  setTwilioRoomSid,
  logTwilioEvent,
  logTwilioError,
  endTwilioSession,
} from "@/utils/twilioLogger/twilioLogger";
import {
  TWILIO_ROOM_CONNECTING,
  TWILIO_ROOM_CONNECTED,
  TWILIO_ROOM_DISCONNECTED,
  TWILIO_ROOM_RECONNECTING,
  TWILIO_ROOM_RECONNECTED,
  TWILIO_ROOM_CONNECT_FAILED,
  TWILIO_PARTICIPANT_CONNECTED,
  TWILIO_PARTICIPANT_DISCONNECTED,
  TWILIO_TRACK_SUBSCRIBED,
  TWILIO_REMOTE_TRACK_STARTED,
  TWILIO_PARTICIPANT_TRACKS_AT_HANDLE,
  TWILIO_TRACK_PRESUBSCRIBED_ATTACHED,
  TWILIO_TRACK_ENABLED,
  TWILIO_TRACK_DISABLED,
  TWILIO_TRACK_SWITCHED_OFF,
  TWILIO_TRACK_SWITCHED_ON,
  TWILIO_TRACK_PUBLICATION_FAILED,
  TWILIO_LOCAL_PERMISSIONS_REQUESTED,
  TWILIO_LOCAL_PERMISSIONS_GRANTED,
  TWILIO_LOCAL_PERMISSIONS_DENIED,
  TWILIO_LOCAL_MIC_TOGGLED,
  TWILIO_LOCAL_DEVICE_REVOKED,
  TWILIO_NETWORK_QUALITY_CHANGED,
  TWILIO_ERROR_SDK,
} from "@/utils/twilioEvents";

export default {
  name: "VideoRoom",
  mixins: [screenWakeLockMixin],
  data() {
    return {
      // asdasdhat@mailinator.com
      video_processors_supported: false,
      video_processor: false,
      participants: [],
      is_request_available: true,
      general_status: "waiting",
      status_message: "Please allow microphone access...",
      connected: false,
      room_object: false,
      loopIntervalId: null,
      meetingStatus: "testRoom",
      meetingStart: null,
      meetingEnd: null,
      permissionsStatus: false,
      permissionsBlocked: false,
      audio_enabled: true,
      video_enabled: true,
      remote_audio_enabled: false,
      duration: 0,
      token: null
    };
  },
  mounted() {
    initialiseTwilioLogger();
    let self = this;
    logTwilioEvent(TWILIO_LOCAL_PERMISSIONS_REQUESTED, {
      device: "microphone",
    });
    navigator.permissions
      .query({ name: "microphone" })
      .then(function (result) {
        if (result.state == "granted") {
          self.status_message = "Establishing audio connection...";
          self.permissionsStatus = true;
          logTwilioEvent(TWILIO_LOCAL_PERMISSIONS_GRANTED, {
            device: "microphone",
          });
        } else if (result.state == "prompt") {
          self.status_message = "Please allow microphone access...";
        } else {
          self.status_message =
            "Please allow microphone permissions and refresh the page...";
          logTwilioEvent(TWILIO_LOCAL_PERMISSIONS_DENIED, {
            device: "microphone",
            reason: "denied",
          });
        }
      })
      .catch((error) => {
        self.status_message = "Please allow microphone access...";
        self.permissionsStatus = false;
        logTwilioEvent(TWILIO_LOCAL_PERMISSIONS_DENIED, {
          device: "microphone",
          reason: error?.name || "unknown",
        });
      });
    this.video_processors_supported = VideoProcessors.isSupported;
    devLog(
      "supported video processors : ",
      this.video_processors_supported,
    );

    // below unfortunetely has to stay for scenario where local track is passed from parent as device tests
    // we could remove that passing media tracks bullshit all together and just destroy and create again in this component
    // we need to think about it, and see how is that in terms of performance
    // both just in case, can't use await here probably we will need to refactor this
    //this aren't triggering in time for them to be attached to the meeting or osmething like that?
    // this.createLocalVideo()
    // this.createLocalAudio()
    //
    this.initializeLocalTracks();

    // this.requestBookingRoom()
  },
  beforeDestroy() {
    this.releaseScreenWakeLockForCall();
    let self = this;
    clearInterval(self.loopIntervalId);
    this.leaveRoom();
    this.$emit("terminateVideoAndAudio");
    endTwilioSession();
    if (this.duration > 60 * 5) {
      this.$root.$emit("show_dc_feedback_modal");
    }
  },
  props: ["booking", "internalBookingId", "coach", "local_audio_track"],
  inject: ["createLocalAudio", "destroyLocalAudio", "createRoomTracks"],
  watch: {},
  computed: {
    supportEmail() {
      return getSupportEmail();
    },
    audioRoomTitle() {
      return this.meetingStatus === "testRoom"
        ? "Waiting Room"
        : this.meetingStatus === "active"
          ? "Session Started"
          : this.meetingStatus === "ended"
            ? "Session Ended"
            : null;
    },
    buttonText() {
      return !this.permissionsStatus
        ? "Accept Permissions"
        : this.meetingStatus === "testRoom"
          ? "Join Meeting"
          : this.meetingStatus === "active"
            ? "End Meeting"
            : this.meetingStatus === "ended"
              ? "Close"
              : null;
    },
  },
  methods: {
    handleButtonClick() {
      // Without permission the only useful action is to ask for it. getUserMedia is
      // the only thing that raises the browser prompt — permissions.query() just
      // reports state, so mounting alone never asks.
      if (!this.permissionsStatus) {
        this.requestPermissions();
        return;
      }
      if (this.meetingStatus === "testRoom") {
        this.requestBookingRoom();
      } else if (this.meetingStatus === "active") {
        this.endSession();
      } else if (this.meetingStatus === "ended") {
        this.closeModal();
      }
    },
    gtag() {
      window.dataLayer.push(arguments);
    },
    sendStartEvent() {
      let self = this;
      this.gtag("event", "appointment-start", {
        user_id: self.$store.getters.user.user.user_id,
        booking_id: self.internalBookingId,
      });
    },
    sendEndEvent() {
      let self = this;
      this.gtag("event", "appointment-end", {
        user_id: self.$store.getters.user.user.user_id,
        booking_id: self.internalBookingId,
        length: moment.utc(self.duration * 1000).format("HH:mm:ss"),
      });
    },
    sendCoachParticipationEvent() {
      let self = this;
      this.gtag("event", "coach-participation", {
        user_id: self.$store.getters.user.user.user_id,
        booking_id: self.internalBookingId,
      });
    },
    async requestBookingRoom(initalCall = false) {
      devLog("bookingRoomReq");
      let self = this;
      if (initalCall) {
        this.status_message = "Joining the meeting ...";
      }
      this.meetingStatus = "active";
      this.acquireScreenWakeLockForCall();
      this.meetingStart = moment();
      if (this.is_request_available) {
        this.is_request_available = false;
        let params = {
          path: "api/u/dmh/get-appointment-access-token",
          appointment_id: this.booking.appointmentId,
          start: formatAppointmentTimeForApi(this.booking.startTime),
          end: formatAppointmentTimeForApi(this.booking.endTime),
          clinician_id: this.booking.clinician.clinicianId,
        };
        let res = await this.api(params);
        this.is_request_available = true;
        if (res.success === true && res.access_token !== null) {
          clearInterval(self.loopIntervalId);
          this.loopIntervalId = null;
          this.token = res.access_token;
          this.joinRoom();
        } else {
          if (this.meetingStatus !== "ended") this.status_message = res.error;
          if (this.loopIntervalId === null) {
            this.loopForRequestingBookingRoom();
            devLog("starting loop");
          }
        }
      }
    },
    loopForRequestingBookingRoom() {
      let self = this;
      if (this.loopIntervalId === null) {
        this.loopIntervalId = setInterval(() => {
          self.requestBookingRoom();
        }, 2000);
      }
    },
    reset(msg = "Meeting will start soon ...") {
      this.is_request_available = true;
      this.general_status = "waiting";
      this.status_message = msg;
      this.connected = false;
    },

    async toggleAudio() {
      // No track when permission was refused — the control is visible but inert.
      if (!this.local_audio_track) return;
      if (this.local_audio_track.isEnabled) {
        this.local_audio_track.disable();
      } else {
        this.local_audio_track.enable();
      }
      this.audio_enabled = this.local_audio_track.isEnabled;
      logTwilioEvent(TWILIO_LOCAL_MIC_TOGGLED, { enabled: this.audio_enabled });
    },
    async leaveRoom(redirectHome = false) {
      //TODO change param to redirect to router routeName
      this.releaseScreenWakeLockForCall();
      this.general_status = "leaving the room in 2 secs";

      if (this.room_object) {
        this.room_object.disconnect();
        this.room_object = false;
      }

      if (this.local_audio_track) {
        await this.destroyLocalAudio();
      }
    },
    async closeModal() {
      // Embedded: the host owns navigation. Routing to our own home would strand the
      // member inside an iframe the host is about to close, and the feedback modal
      // below would pop up inside the partner's frame.
      if (isEmbedded()) {
        notifyPartner({ type: "sl:close", v: 1, reason: "session_ended" });
        return;
      }
      this.$router.push("/");
      // feedback only requested if the session lasted longer than 5 minutes
      const res = await this.api({
        path: "api/u/feature-flag",
        feature_flags: ["spectrum_nps_feedback_questionnaire_show"],
      });
      if (res.success) {
        this.$bvModal.show("feedback-modal");
      }
    },
    endSession() {
      let self = this;
      this.meetingStatus = "ended";
      this.general_status = "ending session";
      clearInterval(self.loopIntervalId);
      let duration = moment().diff(self.meetingStart, "seconds");
      this.duration = duration;
      this.status_message =
        "Thanks for joining - Your session has now ended!<br /><br />Duration: " +
        moment.utc(duration * 1000).format("HH:mm:ss");
      this.sendEndEvent();
      this.leaveRoom();
    },

    async requestPermissions() {
      this.status_message = "Please allow microphone access...";
      await this.initializeLocalTracks();
      if (this.permissionsStatus) return;

      // The prior version assumed any failure meant "blocked" — but the identical
      // failure also happens for a missing mic, one already in use by another app,
      // or a Twilio/network error, none of which "allow it in settings" fixes. Ask
      // the browser what actually happened instead of guessing.
      const denied = await this.isPermissionDenied();
      if (denied) {
        // Genuinely blocked for this origin: the browser won't even prompt again,
        // so retrying can't help — it has to be changed in site settings.
        this.permissionsBlocked = true;
        this.status_message =
          "Microphone access is blocked. Allow it for this site in your browser settings, then reload.";
      } else {
        // Denied is ruled out, so leave the button enabled — this may be transient
        // (device busy, a dropped prompt) and worth letting them retry.
        this.status_message =
          "We couldn't start your microphone. Check nothing else is using it, then try again.";
      }
    },
    // navigator.permissions.query() reports actual browser state, unlike inferring
    // from "the attempt failed" — which is also true for a missing/busy device.
    async isPermissionDenied() {
      try {
        const microphone = await navigator.permissions.query({ name: "microphone" });
        return microphone.state === "denied";
      } catch (e) {
        // Permissions API name unsupported in this browser (e.g. older Firefox) —
        // can't confirm denial, so don't claim it.
        return false;
      }
    },

    async initializeLocalTracks() {
      let audioCreated;
      audioCreated = await this.createLocalAudio();
      if (audioCreated) {
        this.permissionsStatus = true;
      }
    },

    async joinRoom() {
      let tracks = [];

      tracks.push(this.local_audio_track);

      const devices = await navigator.mediaDevices
        .enumerateDevices()
        .catch(() => []);
      const trackLabels = tracks.filter(Boolean).map((t) => t.name || t.kind);
      startTwilioSession({
        platform: "web",
        session_role: "patient",
        sdkVersion: Video.version,
        userAgent: navigator.userAgent,
        isSupported: Video.isSupported,
        devices,
        trackLabels,
      });

      logTwilioEvent(TWILIO_ROOM_CONNECTING);

      try {
        this.room_object = await connect(this.token, { tracks });
        this.meetingStatus = "active";
      } catch (error) {
        if (error instanceof Error) {
          logTwilioError(TWILIO_ROOM_CONNECT_FAILED, error, { stats: null });
        } else {
          logTwilioEvent(TWILIO_ROOM_CONNECT_FAILED, { stats: null });
        }
        return;
      }

      setTwilioRoomSid(this.room_object.name);
      logTwilioEvent(TWILIO_ROOM_CONNECTED, { roomSid: this.room_object.name });

      this.general_status = "connected";

      // SQ-455 diagnostics: timestamp of room connect, used to measure
      // how long the remote-media blackout lasts (see remote track
      // "started" below). P2P ICE setup is the suspected delay.
      const connectedAt = Date.now();

      // Wire LocalTrack "stopped" after publish to detect device revocation
      if (this.local_audio_track) {
        this.local_audio_track.on("stopped", () => {
          logTwilioEvent(TWILIO_LOCAL_DEVICE_REVOKED, { kind: "audio" });
        });
      }

      // Local participant events
      this.room_object.localParticipant.on(
        "trackPublicationFailed",
        async (pubError, localTrack) => {
          const stats = await this.room_object.getStats().catch(() => null);
          logTwilioError(TWILIO_TRACK_PUBLICATION_FAILED, pubError, {
            kind: localTrack.kind,
            stats,
          });
        },
      );

      this.room_object.localParticipant.on(
        "networkQualityLevelChanged",
        (level, networkStats) => {
          logTwilioEvent(TWILIO_NETWORK_QUALITY_CHANGED, {
            level,
            stats: networkStats,
          });
        },
      );

      // SDK-level error handler
      this.room_object.on("error", async (sdkError) => {
        const stats = await this.room_object.getStats().catch(() => null);
        logTwilioError(TWILIO_ERROR_SDK, sdkError, { stats });
      });

      // Room reconnection events
      this.room_object.on("reconnecting", (reconnectError) => {
        logTwilioEvent(TWILIO_ROOM_RECONNECTING, {
          reason: reconnectError?.message,
        });
      });

      this.room_object.on("reconnected", () => {
        logTwilioEvent(TWILIO_ROOM_RECONNECTED);
      });

      const subscribeTrackEvents = (track) => {
        logTwilioEvent(TWILIO_TRACK_SUBSCRIBED, {
          kind: track.kind,
          trackSid: track.sid,
        });
        track.on("started", () =>
          logTwilioEvent(TWILIO_REMOTE_TRACK_STARTED, {
            kind: track.kind,
            trackSid: track.sid,
            msSinceConnected: connectedAt ? Date.now() - connectedAt : null,
          }),
        );
        track.on("enabled", () =>
          logTwilioEvent(TWILIO_TRACK_ENABLED, { kind: track.kind }),
        );
        track.on("disabled", () =>
          logTwilioEvent(TWILIO_TRACK_DISABLED, { kind: track.kind }),
        );
        track.on("switchedOff", () =>
          logTwilioEvent(TWILIO_TRACK_SWITCHED_OFF, { kind: track.kind }),
        );
        track.on("switchedOn", () =>
          logTwilioEvent(TWILIO_TRACK_SWITCHED_ON, { kind: track.kind }),
        );
      };

      // do not attach dataTrack
      const attachTrack = (track) => {
        if (track.kind === "audio") {
          // https://sdk.twilio.com/js/video/releases/2.19.1/docs/RemoteAudioTrack.html
          if (track.isEnabled) {
            this.remote_audio_enabled = true;
          }
          document.getElementById("remote_audio").appendChild(track.attach());
          subscribeTrackEvents(track);
          track.on("enabled", () => {
            this.remote_audio_enabled = true;
          });
          track.on("disabled", () => {
            this.remote_audio_enabled = false;
          });
          track.on("switchedOff", () => {
            this.remote_audio_enabled = false;
          });
          track.on("switchedOn", () => {
            this.remote_audio_enabled = true;
          });
        }
      };

      const handleParticipant = (participant) => {
        // SQ-455 diagnostics: snapshot what tracks exist and which are
        // already subscribed at the instant we begin handling this participant.
        // A non-empty alreadySubscribed list means the pre-fix code path would
        // have dropped those tracks (the race this change closes).
        const publications = Array.from(participant.tracks.values());
        logTwilioEvent(TWILIO_PARTICIPANT_TRACKS_AT_HANDLE, {
          identity: participant.identity,
          totalTracks: publications.length,
          alreadySubscribed: publications
            .filter((publication) => publication.isSubscribed)
            .map((publication) => publication.kind),
          pending: publications
            .filter((publication) => !publication.isSubscribed)
            .map((publication) => publication.kind),
        });

        // Attach any tracks that are ALREADY subscribed by the time we get
        // here. Relying solely on the "trackSubscribed" event loses tracks that
        // subscribed before the listener was registered - the common case when
        // the participant (e.g. the clinician who created the room) is already
        // present at connect time. That race is what leaves remote audio blank
        // on first connect.
        participant.tracks.forEach((publication) => {
          if (publication.isSubscribed && publication.track) {
            logTwilioEvent(TWILIO_TRACK_PRESUBSCRIBED_ATTACHED, {
              kind: publication.kind,
              trackSid: publication.trackSid,
            });
            attachTrack(publication.track);
          }
        });
        // Attach tracks that subscribe after this point.
        participant.on("trackSubscribed", (track) => {
          attachTrack(track);
        });
        participant.on("trackUnsubscribed", (track) => {
          if (track.kind === "audio") {
            track.detach().forEach((element) => element.remove());
          }
        });
      };

      // Participants already in the room when we connected.
      this.room_object.participants.forEach((participant) => {
        handleParticipant(participant);
      });

      // Participants who connect after us.
      this.room_object.on("participantConnected", (participant) => {
        this.sendCoachParticipationEvent();
        logTwilioEvent(TWILIO_PARTICIPANT_CONNECTED, {
          identity: participant.identity,
        });
        handleParticipant(participant);
      });

      this.room_object.on("participantDisconnected", (participant) => {
        logTwilioEvent(TWILIO_PARTICIPANT_DISCONNECTED, {
          identity: participant.identity,
        });
        this.is_request_available = true;
        this.general_status = "disconnected";
        this.endSession();
      });

      this.room_object.on("disconnected", async (room, disconnectError) => {
        const stats = disconnectError
          ? await room.getStats().catch(() => null)
          : null;
        logTwilioEvent(TWILIO_ROOM_DISCONNECTED, {
          reason: disconnectError?.message,
          stats,
        });
        this.is_request_available = false;
        this.general_status = "completed";
        this.endSession();
        this.connected = false;
      });

      this.sendStartEvent();
    },

    logVideoEvent(message) {
      let params = {
        path: "api/u/video/log-info",
        message: (message = JSON.stringify(message)),
      };
      this.api(params);
    },
  },
};
</script>

<style lang="less">
#AudioRoom {
  .audio-room-title {
    font-family: "DMSans Bold";
    font-size: 2.2rem;
    line-height: 2.6rem;
    margin-bottom: 16px;
    letter-spacing: normal;
  }

  .status-message {
    font-family: "DMSans";
    font-size: 1.6rem;
    padding: 12px;
  }

  .audio-room-button {
    font-family: "DMSans Bold";
    font-size: 1.4rem;
    padding: 16px 24px;
    color: #fff;
    border-radius: 40px;
    background-color: #ef2a1d;
    border: 0;
  }

  .text-contact {
    margin: 0 8px;
    width: calc(100% - 16px);
    background-color: rgba(126, 108, 216, 0.5);
    padding: 8px 4px;
    border-radius: 40px;
    color: #fff;
    position: absolute;
    bottom: 8px;
    font-size: 1.5rem;
    text-align: center;
  }

  #video_page {
    background-color: white;

    .img_wrapper {
      text-align: center;
      padding: 0px 20px;

      img {
        border-radius: 50%;
        border: 7px solid #eee;
        box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.09);
      }
    }

    .meeting_time {
      padding: 30px;

      .meeting_time_inner {
        box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.2);
        background-color: #403d56;
        color: white;

        div {
          padding: 6px 10px;
        }

        margin-bottom: 15px;
        border-radius: 15px 15px 0px 15px;

        .day {
          font-size: 17px;
        }

        .hour {
          font-weight: lighter;
          font-size: 14px;
        }

        .date {
          font-size: 30px;
        }

        .month {
          font-size: 17px;
          font-weight: lighter;
        }

        .start_booking {
          position: relative;
          padding: 0px;
          min-height: 25px;

          .start_booking_inner {
            position: absolute;
            right: 0px;
            bottom: 0px;
            background-color: #e83b75;
            padding: 7px 25px;
            border-radius: 15px 0px 0px 0px;

            &:hover {
              cursor: pointer;
            }
          }
        }
      }
    }

    .clock_icon {
      text-align: center;
      font-size: 50px;
    }
  }

  #remote_media_box {
    position: relative;
    min-height: 400px;
    max-height: 400px;

    #remote_media {
      background: #1e1e22;
      max-height: 400px;

      video {
        width: auto;
        height: 400px;
      }
    }

    #remote_media_status_container {
      opacity: 0;
      position: absolute;
      top: 0px;
      left: 30px;
      width: 50px;
      text-align: center;
      height: 200px;

      .remote_vid_button {
        z-index: 1000;
        text-align: center;
        font-size: 20px;
        border-radius: 50%;
        display: inline-block;
        background-color: rgba(0, 230, 184, 0.25);
        width: 50px;
        height: 50px;
        color: white;
        margin-right: 1em;
        margin-top: 15px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;

        &.off {
          background-color: rgba(0, 230, 184, 0.08);
        }
      }
    }

    #remote_media {
      box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
      background-color: #ccc;
      font-size: 0;

      video {
        width: 100%;
        max-width: 100%;
        border-radius: 5px;
        //box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
      }
    }

    .test-local-video-placeholder {
      background: #1e1e22;
      box-shadow: none !important;

      p {
        color: #fff !important;
      }
    }

    #remote_media_placeholder {
      background: #1e1e22;
      border-radius: 5px;
      box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
      min-height: 400px;
      text-align: center;

      h2,
      h4 {
        font-weight: bold;
        color: white;
      }

      p {
        color: white !important;
        font-size: 1.8rem;

        b {
          font-size: 1.6rem;
          font-family: "DMSans Bold";
        }
      }

      video {
        display: none;
      }
    }
  }

  #video_buttons {
    .vid_button {
      position: relative;
      margin-top: 1.5rem;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0 10px;

      .bt_inner {
        border: 0;
        text-align: center;
        font-size: 25px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgba(30, 30, 34, 0.05);
        width: 60px;
        height: 60px;
        color: #122130;
        margin-right: 1em;

        &.off {
          background-color: #1e1e22;
          color: #fff;
        }

        &:hover {
          cursor: pointer;
        }
      }

      span {
        padding-top: 10px;
        position: relative;
        bottom: 5px;
      }
    }
  }

  .utilities_container {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .bg_images {
      img {
        border-radius: 5px;
        box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);

        &:hover {
          cursor: pointer;
        }
      }
    }

    .cta-button {
      max-width: 200px;
      text-align: center;
      margin: 0 auto;
    }
  }

  #local_media,
  .remote_media {
    background-color: whitesmoke;
  }

  .animated.infinite {
    -webkit-animation-iteration-count: infinite;
    animation-iteration-count: infinite;
  }

  .animated {
    -webkit-animation-duration: 1s;
    animation-duration: 1s;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
  }

  .pulse {
    -webkit-animation-name: pulse;
    animation-name: pulse;
  }
}
</style>
